import { NookKind, useNooks, useSearchStream } from "@nook/api-client"
import {
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@nook/ui"
import { nookHost, nookTitle, SourceIcon } from "@nook/client-ui"
import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"

import { useOverlay } from "@/components/app-shell.js"
import { Hint, Keys } from "@/components/keys.js"
import { useLibraryView } from "@/lib/browse.js"

/**
 * ⌘K: every command with its key, and the nooks — the newest five, or what
 * search finds once there is a query. Ask takes the query as its question, and
 * ⌘↵ runs it whichever row is selected.
 */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate()
  const overlay = useOverlay()
  const [view, setView] = useLibraryView()
  const [query, setQuery] = useState("")
  const trimmed = query.trim()
  const { results } = useSearchStream(trimmed)
  const recent = useNooks({ kind: NookKind.Capture }).data?.pages[0]?.items.slice(0, 5) ?? []
  const nooks = trimmed ? results : recent

  const run = (action: () => void) => () => {
    onOpenChange(false)
    setQuery("")
    action()
  }

  const ask = () => void navigate({ to: "/ask/$threadId", params: { threadId: "new" }, search: trimmed ? { q: trimmed } : {} })
  const commands = [
    { label: "New nook", keys: "n", action: () => overlay("capture") },
    { label: trimmed ? `Ask Nook: ${trimmed}` : "Ask Nook", keys: "$mod+Enter", action: ask, always: true },
    // Phones only get the grid (the library's `shown`).
    { label: "Switch grid / list", keys: "v", action: () => setView(view === "grid" ? "compact" : "grid"), className: "max-md:hidden" },
    { label: "Keyboard shortcuts", keys: "[Shift]+?", action: () => overlay("shortcuts") },
  ]
  const places = [
    { label: "Library", keys: "g l", action: () => void navigate({ to: "/" }) },
    { label: "Ask Nook", keys: "g a", action: () => void navigate({ to: "/ask/$threadId", params: { threadId: "new" } }) },
    { label: "Settings", keys: "g s", action: () => void navigate({ to: "/settings" }) },
  ]
  const matches = <T extends { label: string; always?: boolean }>(items: T[]) =>
    items.filter((item) => item.always || item.label.toLowerCase().includes(trimmed.toLowerCase()))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className="top-[120px] translate-y-0 gap-0 overflow-hidden rounded-[14px] p-0 sm:max-w-[640px]"
      >
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <Command
          shouldFilter={false}
          onKeyDown={(event) => {
            if (event.key !== "Enter" || !(event.metaKey || event.ctrlKey)) return
            event.preventDefault()
            run(ask)()
          }}
          className="**:data-[slot=command-input-wrapper]:h-13 **:data-[slot=command-input-wrapper]:px-4"
        >
          <div className="relative">
            <CommandInput value={query} onValueChange={setQuery} placeholder="Search nooks or type a command…" className="text-[0.9375rem]" />
            <Keys keys="Escape" className="absolute top-1/2 right-4 -translate-y-1/2" />
          </div>
          <CommandList className="h-(--cmdk-list-height) max-h-[440px] p-2 transition-[height] duration-150 ease-out motion-reduce:transition-none">
            <CommandEmpty>Nothing matches.</CommandEmpty>
            <Group heading="Actions" items={matches(commands)} run={run} />
            <Group heading="Go to" items={matches(places)} run={run} />
            {nooks.length ? (
              <CommandGroup heading={trimmed ? "Nooks" : "Recent nooks"} className={HEADING}>
                {nooks.map((nook) => (
                  <CommandItem
                    key={nook.id}
                    value={nook.id}
                    onSelect={run(() => void navigate({ to: "/", search: { nook: nook.id } }))}
                    className="h-9.5 gap-3 text-sm"
                  >
                    <SourceIcon icon={nook.sourceIcon} />
                    <span className="flex-1 truncate">{nookTitle(nook)}</span>
                    <span className="text-muted-foreground text-xs">{nookHost(nook)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
          <div className="text-muted-foreground flex h-10 items-center gap-4 border-t px-4 text-xs max-md:hidden">
            <Hint keys="ArrowUp ArrowDown">Move</Hint>
            <Hint keys="Enter">Run</Hint>
            <Hint keys="$mod+Enter">Ask Nook</Hint>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  )
}

const HEADING = "**:[[cmdk-group-heading]]:text-[0.6875rem] **:[[cmdk-group-heading]]:tracking-wide **:[[cmdk-group-heading]]:uppercase"

function Group({
  heading,
  items,
  run,
}: {
  heading: string
  items: { label: string; keys: string; action: () => void; className?: string }[]
  run: (action: () => void) => () => void
}) {
  if (!items.length) return null

  return (
    <CommandGroup heading={heading} className={HEADING}>
      {items.map((item) => (
        <CommandItem key={item.label} value={`${heading} ${item.label}`} onSelect={run(item.action)} className={cn("h-9.5 text-sm", item.className)}>
          <span className="flex-1 truncate">{item.label}</span>
          <Keys keys={item.keys} />
        </CommandItem>
      ))}
    </CommandGroup>
  )
}
